import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { useLandingPageUrl } from "hooks";
import type { OfferProcessingResult } from "hooks/useOffer";
import { invitationCodeRegex, genericInvitationCodeRegex, uidParameterRegex, purlCodeRegex } from "constants/validations";
import { ROUTES } from "routes/constants";
import { useAppDispatch } from "store/hooks";
import { saveLandingPageData, setParameterErrors } from "store/application/applicationSlice";
import type { CampaignData, LoyaltyLandingParams } from "types/OfferData";
import { useProductContext } from "context/ProductContext";

/**
 * Zod schema for common URL parameters
 */
const CommonParametersSchema = z.object({
    cc: z
        .string()
        .regex(/^[\da-z]{3}$/i)
        .optional(),
    sc: z
        .string()
        .regex(/^[\da-z]{1,3}$/i)
        .optional(),
    cmp: z.string().optional(),
    uid: z.string().regex(uidParameterRegex).optional(),
    purl: z.string().regex(purlCodeRegex).optional(),
    skip: z
        .string()
        .regex(/welcome/)
        .optional(),
});

/**
 * Zod schema for MLTA/Loyalty-specific parameters
 */
const LoyaltyParametersSchema = z
    .object({
        jd: z.string().date().optional(),
        my: z
            .string()
            .regex(/^\d{1,2}$/i)
            .optional(),
        mn: z
            .string()
            .regex(/^\d{16}$/i)
            .optional(),
    })
    .merge(CommonParametersSchema);

type CommonParameters = z.infer<typeof CommonParametersSchema>;
type LoyaltyParameters = z.infer<typeof LoyaltyParametersSchema>;

type ParameterValidationResult =
    | {
          success: true;
          isLoyaltyClubOffer: true;
          data: LoyaltyParameters;
      }
    | {
          success: true;
          isLoyaltyClubOffer: false;
          data: CommonParameters;
      }
    | {
          success: false;
          errors: string[];
      };

/**
 * Validates URL parameters using Zod schemas
 * @param params - Map of URL parameters
 * @param isLoyaltyProduct - Whether the current product is MLTA
 * @returns Validation result with parsed data or errors
 */
function validateParameters(params: Map<string, string>, isLoyaltyProduct: boolean): ParameterValidationResult {
    const paramsObject = Object.fromEntries(params);
    const uid = params.get("uid");
    const isClubOffer = uid && genericInvitationCodeRegex.test(uid);

    // For MLTA with club offer, validate with loyalty schema
    if (isLoyaltyProduct && isClubOffer) {
        const result = LoyaltyParametersSchema.safeParse(paramsObject);
        if (result.success) {
            return { success: true, isLoyaltyClubOffer: true, data: result.data };
        }
        return {
            success: false,
            errors: result.error.issues.map((i) => i.path.join(".")),
        };
    }

    // For other cases, validate with common schema
    const result = CommonParametersSchema.safeParse(paramsObject);
    if (result.success) {
        return { success: true, isLoyaltyClubOffer: false, data: result.data };
    }
    return {
        success: false,
        errors: result.error.issues.map((i) => i.path.join(".")),
    };
}

/**
 * Extracts campaign data from validated parameters
 */
function extractCampaignData(params: CommonParameters, url: string): CampaignData {
    return {
        clubCode: params.cc,
        leadSource: params.sc,
        campaignCode: params.cmp,
        campaignUrl: url,
        offerPurl: params.purl,
    };
}

/**
 * Extracts loyalty-specific params from validated parameters
 */
function extractLoyaltyParams(params: LoyaltyParameters): LoyaltyLandingParams {
    return {
        memberSince: params.jd,
        aaaMemberNumber: params.mn,
        membershipLength: params.my ? parseInt(params.my, 10) : undefined,
    };
}

/**
 * Determines if welcome page should be skipped based on parameters
 */
function shouldSkipWelcome(params: CommonParameters): boolean {
    const { uid, purl, skip } = params;
    const hasAutoSkipTrigger = (uid && genericInvitationCodeRegex.test(uid)) || typeof purl !== "undefined";
    return skip === "welcome" || hasAutoSkipTrigger;
}

interface AutomaticOfferLookupOptions {
    onOfferFound: (code: string, isPurl: boolean) => Promise<OfferProcessingResult>;
}

/**
 * Custom hook to handle automatic offer lookup from URL parameters (purl or uid)
 * This runs automatically when the page loads with valid URL parameters.
 * Also extracts and validates campaign/loyalty parameters from URL.
 */
export const useAutomaticOfferLookup = ({ onOfferFound }: AutomaticOfferLookupOptions) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const landingPageParameters = useLandingPageUrl();
    const { isMLTA } = useProductContext();

    const hasUidParam = invitationCodeRegex.test(landingPageParameters.params.get("uid") ?? "");
    const hasPurlParam = !!landingPageParameters.params.get("purl");
    const isAutomaticOfferLookup = hasUidParam || hasPurlParam;

    const [isLoading, setIsLoading] = useState(isAutomaticOfferLookup);
    const [skipWelcome, setSkipWelcome] = useState(false);

    // Process landing page parameters on mount
    useEffect(() => {
        const { url, params } = landingPageParameters;
        const validationResult = validateParameters(params, isMLTA);

        if (!validationResult.success) {
            console.error("Landing page parameter validation failed:", validationResult.errors);
            dispatch(setParameterErrors(validationResult.errors));
            return;
        }

        const campaignData = extractCampaignData(validationResult.data, url);
        const loyaltyParams = validationResult.isLoyaltyClubOffer ? extractLoyaltyParams(validationResult.data) : undefined;

        dispatch(
            saveLandingPageData({
                campaign: campaignData,
                loyaltyParams,
            })
        );

        setSkipWelcome(shouldSkipWelcome(validationResult.data));
    }, [landingPageParameters, dispatch, isMLTA]);

    // Handle automatic offer lookup
    useEffect(() => {
        if (!isAutomaticOfferLookup) {
            setIsLoading(false);
            return;
        }

        const purlParam = landingPageParameters.params.get("purl");
        const uidParam = landingPageParameters.params.get("uid");
        const codeToSubmit = purlParam || uidParam;

        if (!codeToSubmit) {
            setIsLoading(false);
            return;
        }

        const performLookup = async () => {
            try {
                await onOfferFound(codeToSubmit, hasPurlParam);
            } catch (error) {
                console.error("Automatic offer lookup failed:", error);
                if (!hasPurlParam || !hasUidParam) {
                    navigate(ROUTES.SOMETHING_WENT_WRONG);
                }
            } finally {
                setIsLoading(false);
            }
        };

        performLookup();
    }, [isAutomaticOfferLookup, hasPurlParam, hasUidParam, landingPageParameters.params, onOfferFound, navigate]);

    return { isLoading, skipWelcome };
};
